<template>
  <div>
    <vx-card class="mt-base" :title="activityTitle">
      <p class="mb-5">
        Picture this…… you are employed as a Chemical Engineer at the AusFuels Oil Refinery (a made
        up name by the way). The first thing you might ask is “what happens in an oil refinery?
        Well, you can
        <button type="button" id="fakeLink" class="no-style" @click="showLinkPopup()">
          click here
        </button>
        to find out.
      </p>

      <centered-image-wrapper
        :image="require('@/assets/images/experiences/engineering/oil_refinery.jpg')"
        classes="2/3"
        alt=""
      />

      <p class="mb-5">
        Due to a faulty valve, an oil delivery pipe has ruptured and thousands of litres of crude
        oil have leaked out into the bay that the refinery is built on.
      </p>
      <p class="mb-5">
        You have now been tasks with cleaning up the spill. You must attend to both the wildlife and
        the physical environment/landscape. Quite often, Chemical Engineers will test different
        methods of cleaning up spills and that’s what you will do in these tasks.
      </p>
      <p class="mb-5">
        Watch the video below to find out more about the terrible effects oil spills can have on
        birdlife.
      </p>

      <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />

      <h4 class="mb-5">Task 1: Find the Best Way to Clean Oil off Bird Feathers</h4>
      <p class="mb-5">
        In this experiment, you'll look at how oil affects bird feathers and try different clean-up
        methods to find out which works best. You can collect or acquire bird feathers from various
        places - extra marks if you’re able to get feathers that actually came from seabirds!
      </p>

      <h4 class="mb-5">The Resources</h4>
      <p class="mb-5">You’ll need:</p>

      <ul class="default-list">
        <li>Clean, dry feathers (real)</li>
        <li>Four bowls</li>
        <li>Vegetable oil</li>
        <li>Dish-washing detergent</li>
        <li>
          Hot, cold, and room-temperature water (the hot water should be about the same temperature
          you'd use to wash dishes)
        </li>
        <li>1 tablespoon of cocoa powder</li>
      </ul>

      <h4 class="mb-5">The Method</h4>

      <ol class="numbered-list">
        <li>
          Get a bowl and add some vegetable oil. You can mix a little cocoa powder into the
          vegetable oil to make it show up better and look more like crude oil.
        </li>
        <li>
          Fill the bowl with room-temperature water up to about an inch or two below the rim. Pour
          some of the oil onto the water. As oil and water don’t mix, the oil will spread out over
          the water’s surface.
        </li>
        <li>
          Dip some of your feathers into the oil on the water to imitate what happens when a bird
          lands on an oil slick on the ocean.
          <centered-image-wrapper
            :image="require('@/assets/images/experiences/envscience/activity_31.jpg')"
            alt="A child's hand dipping a white feather into a glass bowl of water covered with a dark oil layer; the feather comes out coated in oil."
          />
        </li>
        <li>
          Now it’s time to try the following three methods of cleaning the feathers.
          <ul class="default-list mt-3">
            <li>
              Cold water washing. Put some cold water in a bowl, and then try washing some of the
              oiled feathers in it. Watch what happens.
            </li>
            <li>
              Hot water washing. Put some hot water in a bowl, and then try washing some of the
              oiled feathers in it. Watch what happens.
            </li>
            <li>
              Washing with detergent. Put some hot water and detergent in a bowl, and then try
              cleaning some of the oiled feathers in it. Watch what happens.
            </li>
          </ul>
        </li>
      </ol>

      <p class="mb-5">
        When you’ve finished trying out each of the three cleaning methods, answer the questions
        below.
      </p>
      <p class="mb-2">1. How did the feathers feel when they got oiled?</p>
      <textarea-block v-model="data.question1" :editable="isEditable" />
      <p class="mb-2">2. How do you think this might affect a water bird wearing these feathers?</p>
      <textarea-block v-model="data.question2" :editable="isEditable" />
      <p class="mb-2">
        3. Which method worked best? Imagine that you're washing a real, live bird that has been
        oiled. What things would you need to think about? Which method would you choose to clean the
        bird?
      </p>
      <textarea-block v-model="data.question3" :editable="isEditable" />
      <p class="mb-5">
        <strong>AFTER</strong> you have attempted to answer these questions, you can click the
        button below to view some sample answers.
      </p>
      <button-link-wrapper text="View Sample Answers" @clicked="popupActive = true" />

      <h4 class="mb-5">Task 2: Cleaning Oil Spills:</h4>
      <p class="mb-5">
        As you can see from the previous video, oil spills can have devastating effects on the
        natural world. It’s important to begin clean-up efforts as soon as possible after a spill.
      </p>
      <p class="mb-5">
        First, in the following activity, we’ll take a look at some methods to clean up water after
        oil has been spilled. Begin by watching the video below:
      </p>
      <responsive-iframe-wrapper :video="videos[2].video" :image="videos[2].image" />

      <p class="mb-5">Now, you are going to do the above experiment.</p>
      <h4 class="mb-5">The Resources</h4>
      <p class="mb-5">You’ll need:</p>

      <ul class="default-list">
        <li>Cooking Oil</li>
        <li>Food dye</li>
        <li>Cotton balls</li>
        <li>3 bowls or similar</li>
        <li>Craft sticks</li>
        <li>Plastic spoons</li>
      </ul>
      <h4 class="mb-5">The Method</h4>

      <ol class="numbered-list">
        <li>Place three bowls on top of a baking tray</li>
        <li>Add water to the bowls</li>
        <li>Add some oil to the water to simulate an oil spill</li>
        <li>Add some food dye to the water</li>
      </ol>

      <p class="mb-5">Now you’re ready to test each of the three processes in turn:</p>
      <ul class="default-list">
        <li>Skimming</li>
        <li>Absorbing</li>
        <li>Dispersing</li>
      </ul>
      <p class="mb-5">
        Be sure to watch the video again if you need a reminder on how to perform each process.
      </p>
      <h4 class="mb-5">The Results</h4>
      <p class="mb-5">When you’ve finished your experimentation, answer the questions below:</p>

      <p class="mb-2">
        1. Which of the methods did you find the most effective for removing the oil?
      </p>
      <textarea-block v-model="data.question4" :editable="isEditable" />
      <p class="mb-2">
        2. Which of the methods did you find the least effective for removing the oil?
      </p>
      <textarea-block v-model="data.question5" :editable="isEditable" />
      <p class="mb-2">
        3. Which of these methods do you think would work best on a large scale oil spill at sea?
      </p>
      <textarea-block v-model="data.question6" :editable="isEditable" />
      <p class="mb-2">4. What are the pros and cons of your chosen method?</p>

      <table class="table default-table">
        <tr class="header">
          <th width="50%">PROS</th>
          <th>CONS</th>
        </tr>
        <tr class="body">
          <td><textarea-block v-model="data.question7" :editable="isEditable" /></td>
          <td><textarea-block v-model="data.question8" :editable="isEditable" /></td>
        </tr>
      </table>

      <p class="mb-5">Take a look at this innovative idea!!!</p>
      <responsive-iframe-wrapper :video="videos[3].video" :image="videos[3].image" />

      <save-buttons :is-editable="isEditable" @save="save" />
    </vx-card>

    <vs-popup title="Sample Answers" :active.sync="popupActive">
      <p class="mb-5">1. They became soggy, matted, heavier, etc.</p>
      <p class="mb-5">
        2. Oiling makes it harder for a bird to keep warm, makes them less waterproof, and real
        crude oil can poison a bird if swallowed.
      </p>
      <p class="mb-5">
        3. You probably found that washing the feathers with hot water and detergent was the best
        way to remove the oil from the feathers. However, this actually isn't the best method to use
        when you're washing a live bird. When a bird has been coated with oil, it becomes weak, and
        it isn't able to regulate its body temperature very well. Putting a live bird in hot water
        when it's in this condition can be dangerous for it. People cleaning oiled birds use water
        that's at ambient temperature—the same temperature as the environment around the bird. They
        also make sure to keep the building where they're washing the birds warm: this way, the
        birds don't get too hot or too cold.
      </p>
    </vs-popup>
  </div>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import store from '@/store/store';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/envscience/oil_spills_birds_splash.jpg'),
          video: 'https://www.youtube.com/embed/hWmOA8XIrQE',
        },
        2: {
          image: require('@/assets/images/experiences/envscience/oil_spill_activity_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
            '/content/envscience/oil_spill_activity.mp4',
        },
        3: {
          image: require('@/assets/images/experiences/envscience/reusable_sponge_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
            '/content/envscience/reusable_sponge.mp4',
        },
      },
      popupActive: false,
    };
  },
  methods: {
    ...methods,
    showLinkPopup() {
      store.dispatch('popup/showIframePopup', 'https://en.wikipedia.org/wiki/Oil_refinery');
    },
  },
  computed: computed,
  inject: inject,
};
</script>

<style scoped>
#fakeLink {
  color: #528029; /* 4.7:1 on white; was #80c342 at 2.14:1 */
  font-weight: 500;
  cursor: pointer;
}
</style>
